<template>
  <div>
    <h5>Exportar Ingressos de um Evento</h5>
    <v-alert type="info" dense text>
      <b>Entenda o relatório</b>
      <p class="mb-0">
        O relatório exporta todos os ingressos e entradas de um evento.
      </p>
    </v-alert>
    <v-form v-model="valid" @submit.prevent>
      <v-row>
        <v-col cols="12">
          <party-selector
            v-model="party"
            :disabled="loading"
            :rules="[(v) => v || 'Selecione um evento']"
          />
        </v-col>
        <v-col cols="12">
          <v-select
            label="Status"
            multiple
            v-model="status"
            :items="statuses"
            outlined
            dense
            :rules="[(v) => v.length > 0 || 'Selecione pelo menos um status']"
          />
        </v-col>
      </v-row>
    </v-form>
    <v-alert v-if="error" type="error">
      {{ error }}
    </v-alert>
    <template v-else-if="loading">
      <v-progress-linear
        :indeterminate="totalTickets <= processedTickets"
        :value="(processedTickets / totalTickets) * 100"
        color="primary"
        height="10"
        rounded
      />
      <p class="text-caption text-center mt-1">
        Os ingressos estão sendo processados localmente, não feche a página.
      </p>
    </template>
    <v-alert v-else-if="succeeded" type="success">
      <b class="d-block">
        Processo finalizado com sucesso em {{ exportTime }} segundos
      </b>
      Foram processados {{ processedTickets | number }} ingressos
    </v-alert>
    <div class="d-flex justify-space-between">
      <v-btn @click="$emit('close')" :disabled="loading" text>Fechar</v-btn>
      <v-btn
        @click="exportProcess"
        :disabled="!valid"
        :loading="loading"
        color="primary"
      >
        Exportar
      </v-btn>
    </div>
  </div>
</template>

<script>
import FINANCIAL from "@/services/admin/financial";
import moment from "moment";
import ExcelJS from "exceljs";
import PartySelector from "@/components/global/selector/PartySelector.vue";

export const manifest = {
  name: "Exportar Ingressos de um Evento",
  description: "Exporta todos os ingressos e entradas de um evento",
  icon: "mdi-ticket-outline",
  group: "party",
  formats: ["xlsx"],
};

export default {
  name: "ExportTicketFromParty",
  components: {
    PartySelector,
  },
  data: () => ({
    loading: false,
    error: false,
    valid: false,
    succeeded: false,
    totalTickets: 0,
    processedTickets: 0,
    exportTime: 0,
    status: ["succeeded"],
    statuses: [
      { text: "Aprovado", value: "succeeded" },
      { text: "Reembolsado", value: "refunded" },
      { text: "Cancelado", value: "canceled" },
      { text: "Pendente", value: "pending" },
    ],
  }),
  methods: {
    async exportProcess() {
      const startTime = new Date();
      try {
        this.loading = true;
        this.error = false;
        this.succeeded = false;
        this.totalTickets = 0;
        this.processedTickets = 0;

        let canContinue = true;
        let skip = 0;
        const ticketsList = [];
        while (canContinue) {
          const { tickets, total } = await FINANCIAL.getTickets({
            skip,
            take: 5000,
            partyId: this.party.id,
            status: this.status.join("|"),
          });
          if (total) this.totalTickets = total;
          if (tickets.length === 0) canContinue = false;
          ticketsList.push(...tickets);
          skip += tickets.length;
          this.processedTickets += tickets.length;
        }

        await this.exportExcel(ticketsList);
        this.succeeded = true;
      } catch (error) {
        this.error = error.message || "Erro ao processar pagamentos";
        console.error(error);
      } finally {
        this.loading = false;
        this.exportTime = moment().diff(startTime, "seconds");
      }
    },
    async exportExcel(tickets) {
      const workbook = new ExcelJS.Workbook();

      // Add metadata
      workbook.creator = "TiqOne";
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet("Ingressos");
      worksheet.columns = [
        { header: "#", key: "id", width: 20 },
        { header: "Setor", key: "ticketGroup", width: 20 },
        { header: "Lote", key: "ticketBlock", width: 20 },
        { header: "Valor", key: "amount", width: 20 },
        { header: "Método de Pagamento", key: "paymentMethod", width: 20 },
        { header: "Tipo de Pagamento", key: "paymentType", width: 20 },
        { header: "Data", key: "paymentDate", width: 20 },
        { header: "Status", key: "status", width: 20 },
        { header: "Entrada", key: "ticketEntry", width: 20 },
      ];

      for (const ticket of tickets) {
        const payment = ticket.Payment;
        const ticketBlock = ticket.TicketBlock;
        const ticketGroup = ticketBlock.TicketGroup;

        worksheet.addRow({
          id: ticket.id.slice(0, 8),
          ticketGroup: ticketGroup.name,
          ticketBlock: ticketBlock.name,
          amount: ticket.amount,
          paymentMethod: payment.paymentMethod,
          paymentType: payment.paymentType,
          paymentDate: moment(payment.createdAt).toDate(),
          status: payment.status,
          ticketEntry: ticket._count.TicketEntry ? "Sim" : "Não",
        });
      }
      // format date as date
      worksheet.getColumn("paymentDate").style = { numFmt: "DD/MM/YYYY" };

      // format currency
      worksheet.getColumn("amount").numFmt = "R$ #,##0.00";

      //   Save file
      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Ingressos - ${this.party.name}.xlsx`;
        a.click();
      });
    },
  },
  watch: {
    loading: {
      handler(val) {
        this.$emit("loading", val);
      },
    },
  },
  beforeDestroy() {
    this.$emit("loading", false);
  },
};
</script>
